(() => {
  const slugify = (value, index) => {
    const slug = value
      .toLowerCase()
      .trim()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug || `article-section-${index + 1}`;
  };

  const buildTableOfContents = (root, content) => {
    const headings = Array.from(content.querySelectorAll('h2'));
    const tocLists = Array.from(root.querySelectorAll('[data-article-toc]'));
    const tocContainers = Array.from(root.querySelectorAll('[data-article-toc-container]'));
    const layout = root.querySelector('.article-reading-layout');

    if (!headings.length || !tocLists.length) {
      layout?.classList.add('article-reading-layout--without-toc');
      return;
    }

    tocContainers.forEach((toc) => toc.removeAttribute('hidden'));
    layout?.classList.remove('article-reading-layout--without-toc');

    const usedIds = new Set();
    const linksById = new Map();

    headings.forEach((heading, index) => {
      let id = heading.id || slugify(heading.textContent, index);
      const baseId = id;
      let suffix = 2;

      while (usedIds.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
        id = `${baseId}-${suffix}`;
        suffix += 1;
      }

      heading.id = id;
      heading.tabIndex = -1;
      usedIds.add(id);

      tocLists.forEach((list) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${id}`;
        link.textContent = heading.textContent.trim();
        item.appendChild(link);
        list.appendChild(item);

        if (!linksById.has(id)) linksById.set(id, []);
        linksById.get(id).push(link);

        link.addEventListener('click', () => {
          const mobileToc = link.closest('details');
          if (mobileToc) mobileToc.open = false;
        });
      });
    });

    if (!('IntersectionObserver' in window)) return undefined;

    const setActive = (id) => {
      linksById.forEach((links, linkId) => {
        links.forEach((link) => link.classList.toggle('is-active', linkId === id));
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-18% 0px -68% 0px', threshold: 0 }
    );

    headings.forEach((heading) => observer.observe(heading));
    setActive(headings[0].id);

    return () => observer.disconnect();
  };

  const enhanceRichText = (content) => {
    content.querySelectorAll('p').forEach((paragraph) => {
      if (!paragraph.textContent.trim() && !paragraph.querySelector('img, video, iframe')) {
        paragraph.remove();
        return;
      }

      const label = paragraph.querySelector('strong:first-child')?.textContent.trim().toLowerCase();

      if (label?.startsWith('pro tip')) {
        paragraph.classList.add('article-callout', 'article-callout--tip');
      }

      if (label?.startsWith('karmabless pick')) {
        paragraph.classList.add('article-callout', 'article-callout--pick');
      }
    });

    content.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('article-table-scroll')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'article-table-scroll';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });

    content.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.decoding = 'async';
    });
  };

  const setupReadingProgress = (root, content) => {
    const progress = root.querySelector('[data-reading-progress]');
    if (!progress) return;

    let ticking = false;

    const update = () => {
      const contentRect = content.getBoundingClientRect();
      const start = contentRect.top + window.scrollY;
      const end = contentRect.bottom + window.scrollY - window.innerHeight;
      const distance = Math.max(end - start, 1);
      const value = Math.min(1, Math.max(0, (window.scrollY - start) / distance));
      progress.style.width = `${value * 100}%`;
      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  };

  const initializeArticle = (root) => {
    if (!root || root.dataset.articleReadingReady === 'true') return;

    const content = root.querySelector('[data-article-content]');
    if (!content) return;

    root.dataset.articleReadingReady = 'true';
    enhanceRichText(content);
    const cleanupToc = buildTableOfContents(root, content);
    const cleanupProgress = setupReadingProgress(root, content);
    root.articleReadingCleanup = () => {
      cleanupToc?.();
      cleanupProgress?.();
    };
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-article-reading]').forEach(initializeArticle);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-article-reading]').forEach((root) => {
      root.articleReadingCleanup?.();
    });
  });
})();
